import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Platform as RNPlatform } from 'react-native';
import { Card, Text, Button, IconButton, Portal, Modal, useTheme } from 'react-native-paper';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const INSTALL_PROMPT_DISMISSED_KEY = 'install_prompt_dismissed';
const DISMISS_COUNT_KEY = 'install_prompt_dismiss_count';
const MAX_DISMISSALS = 3; // Stop showing after 3 dismissals

export const InstallPrompt: React.FC = () => {
  const theme = useTheme();
  const [showPrompt, setShowPrompt] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Only run on web platform
    if (RNPlatform.OS !== 'web') {
      console.log('[InstallPrompt] Not web platform, skipping');
      return;
    }

    console.log('[InstallPrompt] Initializing...');
    checkInstallStatus();

    // Set up event listener for Android install prompt
    const handleBeforeInstallPrompt = (e: Event): void => {
      console.log('[InstallPrompt] beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const checkInstallStatus = async (): Promise<void> => {
    try {
      // Check if running in standalone mode (already installed)
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://');

      console.log('[InstallPrompt] Standalone mode:', standalone);
      setIsStandalone(standalone);

      if (standalone) {
        console.log('[InstallPrompt] Already installed, not showing prompt');
        return; // Don't show prompt if already installed
      }

      // Detect platform
      const userAgent = window.navigator.userAgent.toLowerCase();
      const iOS = /iphone|ipad|ipod/.test(userAgent);
      const android = /android/.test(userAgent);

      console.log('[InstallPrompt] Platform detection - iOS:', iOS, 'Android:', android);
      console.log('[InstallPrompt] User agent:', userAgent);

      setIsIOS(iOS);
      setIsAndroid(android);

      // Check if user has dismissed too many times
      const dismissCountStr = await AsyncStorage.getItem(DISMISS_COUNT_KEY);
      const dismissCount = dismissCountStr ? parseInt(dismissCountStr, 10) : 0;

      console.log('[InstallPrompt] Dismiss count:', dismissCount);

      if (dismissCount >= MAX_DISMISSALS) {
        console.log('[InstallPrompt] Max dismissals reached, not showing prompt');
        return; // Stop showing if dismissed too many times
      }

      const dismissed = await AsyncStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY);
      console.log('[InstallPrompt] Previously dismissed in session:', dismissed);

      if (dismissed === 'true') {
        console.log('[InstallPrompt] Dismissed in current session, not showing prompt');
        return; // User dismissed in current session
      }

      // For iOS or Android without native prompt, always show our custom prompt
      // Note: Chrome's beforeinstallprompt may not fire immediately due to engagement heuristics
      // (requires user interaction and ~30 seconds on site)
      if (iOS || android) {
        console.log('[InstallPrompt] Will show prompt in 3 seconds...');
        setTimeout(() => {
          console.log('[InstallPrompt] Showing prompt now!');
          setShowPrompt(true);
        }, 3000);
      }
    } catch (error) {
      console.error('[InstallPrompt] Error checking install status:', error);
      // Still try to show prompt on error
      setTimeout(() => {
        console.log('[InstallPrompt] Showing prompt despite error');
        setShowPrompt(true);
      }, 3000);
    }
  };

  const handleInstall = async (): Promise<void> => {
    console.log('[InstallPrompt] Install button clicked');
    console.log('[InstallPrompt] isAndroid:', isAndroid, 'deferredPrompt:', !!deferredPrompt);

    if (isAndroid && deferredPrompt) {
      // Show native Android install prompt
      console.log('[InstallPrompt] Showing native Android install prompt');
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      console.log('[InstallPrompt] User choice:', outcome);
      if (outcome === 'accepted') {
        console.log('[InstallPrompt] User accepted the install prompt');
      }

      setDeferredPrompt(null);
      setShowPrompt(false);
    } else {
      // Show instructions for iOS or if native prompt not available
      console.log('[InstallPrompt] Showing manual instructions');
      setShowInstructions(true);
    }
  };

  const handleDismiss = async (): Promise<void> => {
    try {
      // Increment dismiss count
      const dismissCountStr = await AsyncStorage.getItem(DISMISS_COUNT_KEY);
      const dismissCount = dismissCountStr ? parseInt(dismissCountStr, 10) : 0;
      await AsyncStorage.setItem(DISMISS_COUNT_KEY, (dismissCount + 1).toString());

      // Set dismissed flag for current session
      await AsyncStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, 'true');
    } catch (error) {
      console.error('Error saving dismiss state:', error);
    }

    setShowPrompt(false);
  };

  const handleNotNow = (): void => {
    setShowPrompt(false);
    // Will show again next session
  };

  // Only render on web platform
  if (RNPlatform.OS !== 'web') {
    return null;
  }

  if (isStandalone || !showPrompt) {
    return null;
  }

  console.log('[InstallPrompt] Rendering prompt card');

  return (
    <>
      <Card style={styles.promptCard} elevation={4}>
        <Card.Content>
          <View style={styles.header}>
            <Icon name="download" size={24} color={theme.colors.primary} />
            <Text variant="titleMedium" style={styles.title}>
              Install Ocean Insight Pro
            </Text>
            <IconButton
              icon="close"
              size={20}
              onPress={handleDismiss}
              style={styles.closeButton}
            />
          </View>

          <Text variant="bodyMedium" style={styles.description}>
            Install our app for the best experience:
          </Text>

          <View style={styles.features}>
            <View style={styles.feature}>
              <Text variant="bodySmall" style={styles.checkmark}>✓</Text>
              <Text variant="bodySmall" style={styles.featureText}>Faster loading</Text>
            </View>
            <View style={styles.feature}>
              <Text variant="bodySmall" style={styles.checkmark}>✓</Text>
              <Text variant="bodySmall" style={styles.featureText}>Full screen mode</Text>
            </View>
            <View style={styles.feature}>
              <Text variant="bodySmall" style={styles.checkmark}>✓</Text>
              <Text variant="bodySmall" style={styles.featureText}>Home screen access</Text>
            </View>
          </View>

          <View style={styles.buttons}>
            <Button
              mode="contained"
              onPress={handleInstall}
              style={styles.installButton}
              icon="download"
            >
              Install
            </Button>
            <Button
              mode="text"
              onPress={handleNotNow}
              style={styles.laterButton}
            >
              Not Now
            </Button>
          </View>
        </Card.Content>
      </Card>

      <Portal>
        <Modal
          visible={showInstructions}
          onDismiss={() => setShowInstructions(false)}
          contentContainerStyle={styles.modalContent}
        >
          <ScrollView>
            <View style={styles.modalHeader}>
              <Text variant="headlineSmall" style={styles.modalTitle}>
                Install Ocean Insight Pro
              </Text>
              <IconButton
                icon="close"
                size={24}
                onPress={() => setShowInstructions(false)}
              />
            </View>

            {isIOS ? (
              <View style={styles.instructions}>
                <Text variant="titleMedium" style={styles.instructionTitle}>
                  For iPhone/iPad (Safari):
                </Text>

                <View style={styles.step}>
                  <View style={styles.stepNumber}>
                    <Text style={styles.stepNumberText}>1</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <View style={styles.stepHeader}>
                      <Icon name="share" size={20} color={theme.colors.primary} />
                      <Text variant="bodyLarge" style={styles.stepText}>
                        Tap the Share button
                      </Text>
                    </View>
                    <Text variant="bodySmall" style={styles.stepDescription}>
                      Look for the square with an arrow pointing up at the bottom of Safari
                    </Text>
                  </View>
                </View>

                <View style={styles.step}>
                  <View style={styles.stepNumber}>
                    <Text style={styles.stepNumberText}>2</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <View style={styles.stepHeader}>
                      <Icon name="plus-box" size={20} color={theme.colors.primary} />
                      <Text variant="bodyLarge" style={styles.stepText}>
                        Select "Add to Home Screen"
                      </Text>
                    </View>
                    <Text variant="bodySmall" style={styles.stepDescription}>
                      Scroll down in the share menu to find this option
                    </Text>
                  </View>
                </View>

                <View style={styles.step}>
                  <View style={styles.stepNumber}>
                    <Text style={styles.stepNumberText}>3</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <View style={styles.stepHeader}>
                      <Icon name="check-circle" size={20} color={theme.colors.primary} />
                      <Text variant="bodyLarge" style={styles.stepText}>
                        Tap "Add"
                      </Text>
                    </View>
                    <Text variant="bodySmall" style={styles.stepDescription}>
                      The app icon will appear on your home screen
                    </Text>
                  </View>
                </View>

                <Card style={styles.noteCard}>
                  <Card.Content>
                    <View style={styles.noteHeader}>
                      <Icon name="information" size={20} color={theme.colors.primary} />
                      <Text variant="titleSmall" style={styles.noteTitle}>Note</Text>
                    </View>
                    <Text variant="bodySmall">
                      This feature only works in Safari browser. If you're using Chrome or another browser, please open this page in Safari.
                    </Text>
                  </Card.Content>
                </Card>
              </View>
            ) : (
              <View style={styles.instructions}>
                <Text variant="titleMedium" style={styles.instructionTitle}>
                  For Android:
                </Text>

                <View style={styles.step}>
                  <View style={styles.stepNumber}>
                    <Text style={styles.stepNumberText}>1</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <View style={styles.stepHeader}>
                      <Icon name="dots-vertical" size={20} color={theme.colors.primary} />
                      <Text variant="bodyLarge" style={styles.stepText}>
                        Tap the menu button
                      </Text>
                    </View>
                    <Text variant="bodySmall" style={styles.stepDescription}>
                      Look for the three dots (⋮) in the top right corner
                    </Text>
                  </View>
                </View>

                <View style={styles.step}>
                  <View style={styles.stepNumber}>
                    <Text style={styles.stepNumberText}>2</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <View style={styles.stepHeader}>
                      <Icon name="plus-box" size={20} color={theme.colors.primary} />
                      <Text variant="bodyLarge" style={styles.stepText}>
                        Select "Install app" or "Add to Home screen"
                      </Text>
                    </View>
                    <Text variant="bodySmall" style={styles.stepDescription}>
                      Chrome shows "Install app", other browsers may say "Add to Home screen"
                    </Text>
                  </View>
                </View>

                <View style={styles.step}>
                  <View style={styles.stepNumber}>
                    <Text style={styles.stepNumberText}>3</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <View style={styles.stepHeader}>
                      <Icon name="check-circle" size={20} color={theme.colors.primary} />
                      <Text variant="bodyLarge" style={styles.stepText}>
                        Confirm installation
                      </Text>
                    </View>
                    <Text variant="bodySmall" style={styles.stepDescription}>
                      Tap "Install" in the popup dialog
                    </Text>
                  </View>
                </View>
              </View>
            )}

            <Button
              mode="contained"
              onPress={() => setShowInstructions(false)}
              style={styles.gotItButton}
            >
              Got it!
            </Button>
          </ScrollView>
        </Modal>
      </Portal>
    </>
  );
};

const styles = StyleSheet.create({
  promptCard: {
    margin: 16,
    marginTop: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    flex: 1,
    marginLeft: 8,
    fontWeight: '600',
  },
  closeButton: {
    margin: 0,
  },
  description: {
    marginBottom: 12,
  },
  features: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  feature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  checkmark: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4CAF50',
  },
  featureText: {
    fontSize: 12,
  },
  buttons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  installButton: {
    flex: 1,
  },
  laterButton: {
    flex: 1,
  },
  modalContent: {
    backgroundColor: 'white',
    margin: 20,
    padding: 20,
    borderRadius: 8,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    flex: 1,
    fontWeight: '600',
  },
  instructions: {
    gap: 16,
  },
  instructionTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  step: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#0066CC',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumberText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 14,
  },
  stepContent: {
    flex: 1,
    gap: 4,
  },
  stepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  stepText: {
    fontWeight: '600',
  },
  stepDescription: {
    color: '#666',
    marginLeft: 28,
  },
  noteCard: {
    backgroundColor: '#E3F2FD',
    marginTop: 8,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  noteTitle: {
    fontWeight: '600',
  },
  gotItButton: {
    marginTop: 20,
  },
});
